import { useEffect } from 'react';

/**
 * macOS/iOS-style keyboard shortcuts
 * Following Apple's shortcuts conventions
 */

interface KeyboardShortcutCallbacks {
  onSearch?: () => void;
  onToggleFilters?: () => void;
  onEscape?: () => void;
  onSelectAll?: () => void;
}

export function useKeyboardShortcuts(callbacks: KeyboardShortcutCallbacks) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + K: Focus search (like Spotlight)
      if (modKey && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
        callbacks.onSearch?.();
      }

      // Cmd/Ctrl + F: Toggle filters
      if (modKey && e.key === 'f') {
        e.preventDefault();
        callbacks.onToggleFilters?.();
      }

      // Escape: Clear selection / Close modals
      if (e.key === 'Escape') {
        callbacks.onEscape?.();
      }

      // Cmd/Ctrl + A: Select all visible invoices
      if (modKey && e.key === 'a') {
        // Check if we're in the dashboard context (not in a text input)
        const activeElement = document.activeElement;
        const isInputFocused =
          activeElement?.tagName === 'INPUT' ||
          activeElement?.tagName === 'TEXTAREA' ||
          (activeElement as HTMLElement)?.isContentEditable;

        if (!isInputFocused) {
          e.preventDefault();
          callbacks.onSelectAll?.();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [callbacks]);
}

/**
 * Get the keyboard shortcut hint based on platform
 */
export function getShortcutHint(action: 'search' | 'filter' | 'selectAll'): string {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modKey = isMac ? '⌘' : 'Ctrl';

  const shortcuts: Record<string, string> = {
    search: `${modKey}K`,
    filter: `${modKey}F`,
    selectAll: `${modKey}A`
  };

  return shortcuts[action] || '';
}
